import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  ApiGetListResponse,
  CreateTenantPayload,
  CreateTenantRequest,
  ILanguage,
  ITenant,
  ITenantLanguage,
  ITenantThemeConfig,
  SettingKeyCreateRequest,
  TenantDto,
  TenantLanguageCreateItem,
  TenantLanguageListParams,
  TenantLanguagePaginatedResponse,
  TenantLanguageUpdateRequest,
  TenantListParams,
  TenantListResponseDto,
  TenantLocalizedDataMap,
  TenantSettingDto,
  TenantSettingResponse,
  TenantSettingUpdateRequest,
  UpdateTenantPayload,
  UpdateTenantRequest,
} from '@/shared/api/types/tenant-types'
import i18next from 'i18next'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { mapTenantDto } from '@/shared/api/mappers/tenant-mapper'
import { buildTenantHeaderConfig } from '@/shared/api/utils/tenant-header'
import { toNullable } from '@/shared/api/utils/to-nullable'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { TenantSettingKey } from '@/shared/constants/tenant-settings'
import { request } from '@/shared/libs/request'

const TENANT_PATH = 'tenants'
const TENANT_LANGUAGES_PATH = 'tenant-languages'
const TENANT_SETTINGS_PATH = 'tenant-settings'
const LANGUAGES_PATH = 'languages'
const TOURS_PATH = 'admin/tours/default'
const iam = GW.iam
const packaging = GW.packaging

function parseSettingValue<T>(rawValue: string | null | undefined, fallback: T): T {
  if (!rawValue) {
    return fallback
  }

  try {
    return JSON.parse(rawValue) as T
  } catch {
    return fallback
  }
}

function normalizeThemeConfig(
  rawThemeConfig: ITenantThemeConfig | null,
): ITenantThemeConfig | null {
  if (!rawThemeConfig) {
    return null
  }

  const raw = rawThemeConfig as ITenantThemeConfig & {
    logo_url?: string
    favicon_url?: string
  }

  return {
    ...rawThemeConfig,
    ...(raw.logoUrl || raw.logo_url ? { logoUrl: raw.logoUrl ?? raw.logo_url } : {}),
    ...(raw.faviconUrl || raw.favicon_url ? { faviconUrl: raw.faviconUrl ?? raw.favicon_url } : {}),
  }
}

function normalizeTenantListResponse(
  page: PaginatedResponse<TenantDto> | TenantListResponseDto,
  params: TenantListParams,
): PaginatedResponse<ITenant> {
  const tenantDtos = 'items' in page && page.items ? page.items : (page.data ?? [])

  return {
    data: tenantDtos.map(mapTenantDto),
    total: page.total ?? tenantDtos.length,
    current: page.current ?? params.page,
    keyword: page.keyword ?? null,
    pageSize: page.pageSize ?? params.pageSize,
  }
}

function normalizeLanguageResponse(
  payload: ApiResponseEnvelope<ILanguage[]> | ILanguage[],
): ILanguage[] {
  return Array.isArray(payload) ? payload : unwrapApiEnvelope(payload)
}

function normalizeTenantLanguageResponse(
  payload: ApiResponseEnvelope<TenantLanguagePaginatedResponse> | TenantLanguagePaginatedResponse,
): ITenantLanguage[] {
  const unwrapped = isTenantLanguagePaginatedResponse(payload)
    ? payload
    : unwrapApiEnvelope(payload)

  return unwrapped.items
}

function normalizeTenantLanguagePageResponse(
  payload: ApiResponseEnvelope<TenantLanguagePaginatedResponse> | TenantLanguagePaginatedResponse,
): PaginatedResponse<ITenantLanguage> {
  const unwrapped = isTenantLanguagePaginatedResponse(payload)
    ? payload
    : unwrapApiEnvelope(payload)

  return {
    data: unwrapped.items,
    total: unwrapped.total,
    current: Math.floor((unwrapped.skip ?? 0) / (unwrapped.limit || 1)) + 1,
    pageSize: unwrapped.limit,
    keyword: null,
  }
}

function isTenantLanguagePaginatedResponse(
  payload: unknown,
): payload is TenantLanguagePaginatedResponse {
  if (!payload || typeof payload !== 'object') return false
  const value = payload as Partial<TenantLanguagePaginatedResponse>
  return (
    Array.isArray(value.items) &&
    typeof value.total === 'number' &&
    typeof value.skip === 'number' &&
    typeof value.limit === 'number' &&
    value.limit > 0
  )
}

export interface UpdateDefaultTourSettingPayload {
  tenantId?: string
  enabled: boolean
  destinationId: string
}

export interface DefaultTourSettingResponse {
  enabled: boolean
  destinationId: string
}

export async function getDefaultTourSetting(
  tenantId?: string,
): Promise<ApiResponse<DefaultTourSettingResponse>> {
  const response = await request.get<ApiResponseEnvelope<DefaultTourSettingResponse>>(
    packaging(TOURS_PATH),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function updateDefaultTourSetting(
  payload: UpdateDefaultTourSettingPayload,
): Promise<ApiResponse<unknown | null>> {
  const response = await request.put<ApiResponseEnvelope<unknown | null>>(
    packaging(TOURS_PATH),
    { enabled: payload.enabled, destinationId: payload.destinationId },
    buildTenantHeaderConfig(payload.tenantId),
  )

  return { data: unwrapApiEnvelope(response.data) }
}

async function getTenantSettings(tenantId: string) {
  try {
    const response = await request.get<ApiGetListResponse<TenantSettingResponse>>(
      iam(TENANT_SETTINGS_PATH),
      buildTenantHeaderConfig(tenantId),
    )
    return response?.data?.data?.data || []
  } catch {
    return []
  }
}

function enrichTenantWithSettings(tenant: ITenant, settings: TenantSettingDto[]): ITenant {
  const settingsMap = new Map(settings.map((setting) => [setting.settingKey, setting.settingValue]))

  const themeConfig = normalizeThemeConfig(
    parseSettingValue<ITenantThemeConfig | null>(settingsMap.get('theme_config'), null),
  )
  const supportedLanguages = parseSettingValue<string[] | null>(
    settingsMap.get('supported_languages'),
    null,
  )

  return {
    ...tenant,
    ...(themeConfig ? { themeConfig } : {}),
    ...(supportedLanguages ? { supportedLanguages } : {}),
  }
}

export async function getTenants(params: TenantListParams): Promise<PaginatedResponse<ITenant>> {
  const qs = toQueryString({
    current: params.page,
    pageSize: params.pageSize,
    ...(params.search ? { keywords: params.search } : {}),
    ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
  })
  const response = await request.get<
    ApiResponseEnvelope<PaginatedResponse<TenantDto> | TenantListResponseDto>
  >(iam(`${TENANT_PATH}/all?${qs}`))

  return normalizeTenantListResponse(unwrapApiEnvelope(response.data), params)
}

export async function getTenantById(
  tenantId: string,
  options?: { withSettings?: boolean },
): Promise<ApiResponse<ITenant>> {
  if (options?.withSettings) {
    const [tenantResponse, settings] = await Promise.all([
      request.get<ApiResponseEnvelope<TenantDto>>(
        iam(TENANT_PATH),
        buildTenantHeaderConfig(tenantId),
      ),
      getTenantSettings(tenantId),
    ])
    const mappedTenant = mapTenantDto(unwrapApiEnvelope(tenantResponse.data))
    return { data: enrichTenantWithSettings(mappedTenant, settings) }
  }

  const tenantResponse = await request.get<ApiResponseEnvelope<TenantDto>>(
    iam(`${TENANT_PATH}`),
    buildTenantHeaderConfig(tenantId),
  )
  return { data: mapTenantDto(unwrapApiEnvelope(tenantResponse.data)) }
}

export async function getTenantLanguages(
  tenantId?: string,
  params: TenantLanguageListParams = {},
): Promise<ApiResponse<ITenantLanguage[]>> {
  const qs = toQueryString({ ...params })
  const response = await request.get<ApiResponseEnvelope<TenantLanguagePaginatedResponse>>(
    iam(qs ? [TENANT_LANGUAGES_PATH, qs].join('?') : TENANT_LANGUAGES_PATH),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: normalizeTenantLanguageResponse(response.data),
  }
}

export async function getTenantLanguagesPage(
  tenantId: string,
  params: TenantLanguageListParams,
): Promise<PaginatedResponse<ITenantLanguage>> {
  const qs = toQueryString({ ...params })
  const response = await request.get<ApiResponseEnvelope<TenantLanguagePaginatedResponse>>(
    iam(qs ? [TENANT_LANGUAGES_PATH, qs].join('?') : TENANT_LANGUAGES_PATH),
    buildTenantHeaderConfig(tenantId),
  )
  return normalizeTenantLanguagePageResponse(response.data)
}

export async function getLanguages(): Promise<ApiResponse<ILanguage[]>> {
  const response = await request.get<ApiResponseEnvelope<ILanguage[]> | ILanguage[]>(
    GW.masterdata(LANGUAGES_PATH),
  )
  return {
    data: normalizeLanguageResponse(response.data),
  }
}
// Cần xem lại sau khi Truyền sửa tables
export async function getIamLanguages(): Promise<ApiResponse<ILanguage[]>> {
  const response = await request.get<ApiResponseEnvelope<ILanguage[]> | ILanguage[]>(
    iam(LANGUAGES_PATH),
  )
  return {
    data: normalizeLanguageResponse(response.data),
  }
}

export async function createTenantLanguages(
  payload: TenantLanguageCreateItem[],
  tenantId?: string,
): Promise<ApiResponse<ITenantLanguage[]>> {
  const response = await request.post<ApiResponseEnvelope<ITenantLanguage[]> | ITenantLanguage[]>(
    iam(TENANT_LANGUAGES_PATH),
    payload,
    buildTenantHeaderConfig(tenantId),
  )
  const data = Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
  return {
    data,
    message:
      (Array.isArray(response.data) ? null : response.data.message) ??
      i18next.t('tenant.language.createSuccess'),
  }
}

export async function deleteTenantLanguage(
  tenantLanguageId: string,
  tenantId?: string,
): Promise<ApiResponse<void>> {
  const response = await request.delete<ApiResponseEnvelope<null>>(
    iam(`${TENANT_LANGUAGES_PATH}/${tenantLanguageId}`),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: undefined,
    message: response.data?.message ?? i18next.t('tenant.language.deleteSuccess'),
  }
}

export async function createTenant(payload: CreateTenantPayload): Promise<ApiResponse<ITenant>> {
  const requestPayload: CreateTenantRequest = {
    name: payload.name,
    slug: payload.slug,
    defaultLanguage: payload.defaultLanguage,
    description: toNullable(payload.description),
    logoUrl: toNullable(payload.logoUrl),
    website: toNullable(payload.website),
    contactEmail: toNullable(payload.contactEmail),
    contactPhone: toNullable(payload.phone),
  }

  const response = await request.post<ApiResponseEnvelope<TenantDto>>(
    iam(TENANT_PATH),
    requestPayload,
  )
  const createdTenant = mapTenantDto(unwrapApiEnvelope(response.data))

  return {
    data: createdTenant,
    message: response.data.message ?? i18next.t('tenant.createSuccess'),
  }
}

export async function updateTenant(
  id: string,
  payload: UpdateTenantPayload,
): Promise<ApiResponse<ITenant>> {
  const requestPayload: UpdateTenantRequest = {
    ...(payload.name !== undefined ? { name: toNullable(payload.name) } : {}),
    ...(payload.slug !== undefined ? { slug: toNullable(payload.slug) } : {}),
    ...(payload.description !== undefined ? { description: toNullable(payload.description) } : {}),
    ...(payload.logoUrl !== undefined ? { logoUrl: toNullable(payload.logoUrl) } : {}),
    ...(payload.website !== undefined ? { website: toNullable(payload.website) } : {}),
    ...(payload.contactEmail !== undefined
      ? { contactEmail: toNullable(payload.contactEmail) }
      : {}),
    ...(payload.phone !== undefined ? { contactPhone: toNullable(payload.phone) } : {}),
    ...(payload.isActive !== undefined ? { isActive: payload.isActive } : {}),
  }

  if (Object.keys(requestPayload).length === 0) {
    return getTenantById(id)
  }

  const response = await request.patch<ApiResponseEnvelope<TenantDto>>(
    iam(TENANT_PATH),
    requestPayload,
    buildTenantHeaderConfig(id),
  )

  return {
    data: mapTenantDto(unwrapApiEnvelope(response.data)),
    message: response.data.message ?? i18next.t('tenant.updateSuccess'),
  }
}

export async function updateTenantInfo(
  id: string,
  localizedData: TenantLocalizedDataMap,
): Promise<ApiResponse<ITenant>> {
  await request.patch<ApiResponseEnvelope<TenantDto>>(
    iam(`${TENANT_PATH}/info`),
    { localizedData },
    buildTenantHeaderConfig(id),
  )
  return getTenantById(id)
}

export async function deleteTenant(tenantId: string): Promise<ApiResponse<void>> {
  const response = await request.delete<ApiResponseEnvelope<null>>(
    iam(TENANT_PATH),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: undefined,
    message: response.data?.message ?? i18next.t('tenant.deleteSuccess'),
  }
}

export async function toggleTenantStatus(id: string): Promise<ApiResponse<ITenant>> {
  const currentTenant = await getTenantById(id)
  await request.patch<ApiResponseEnvelope<TenantDto>>(
    iam(TENANT_PATH),
    {
      isActive: !currentTenant.data.isActive,
    },
    buildTenantHeaderConfig(id),
  )

  return getTenantById(id)
}

async function updateTenantSetting(
  id: string,
  settingKey: TenantSettingKey,
  settingValue: string,
): Promise<void> {
  const payload: TenantSettingUpdateRequest = {
    settingValue,
  }

  await request.put<TenantSettingDto>(
    iam(`${TENANT_SETTINGS_PATH}/settings/${settingKey}`),
    payload,
    buildTenantHeaderConfig(id),
  )
}

export async function updateTenantTheme(
  id: string,
  themeConfig: ITenantThemeConfig,
): Promise<ApiResponse<ITenant>> {
  await updateTenantSetting(id, TenantSettingKey.THEME_CONFIG, JSON.stringify(themeConfig))

  return getTenantById(id)
}

export async function addTenantLanguage(
  id: string,
  languageCode: string,
): Promise<ApiResponse<ITenantLanguage[]>> {
  const currentTenantLanguages = await getTenantLanguages(id)
  if (currentTenantLanguages.data.some((item) => item.languageCode === languageCode)) {
    return currentTenantLanguages
  }

  const languagesResponse = await getIamLanguages()
  const language = languagesResponse.data.find((item) => item.code === languageCode)
  if (!language) {
    return currentTenantLanguages
  }

  const createItem: TenantLanguageCreateItem = {
    languageId: language.id,
    isDefault: false,
    isActive: true,
    sortOrder: currentTenantLanguages.data.length,
  }

  await createTenantLanguages([createItem], id)

  return getTenantLanguages(id)
}

export async function reorderTenantLanguages(
  id: string,
  updates: TenantLanguageUpdateRequest[],
): Promise<ApiResponse<ITenantLanguage[]>> {
  if (updates.length > 0) {
    await request.patch(iam(TENANT_LANGUAGES_PATH), updates, buildTenantHeaderConfig(id))
  }

  return getTenantLanguages(id)
}

export async function updateTenantDefaultLanguage(
  id: string,
  languageCode: string,
): Promise<ApiResponse<ITenant>> {
  const currentTenantLanguages = await getTenantLanguages(id)
  const currentDefault = currentTenantLanguages.data.find((item) => item.isDefault)
  const target = currentTenantLanguages.data.find((item) => item.languageCode === languageCode)

  if (!target || target.id === currentDefault?.id) {
    return getTenantById(id)
  }

  const updates: TenantLanguageUpdateRequest[] = [
    ...(currentDefault ? [{ tenantLanguageId: currentDefault.id, isDefault: false }] : []),
    { tenantLanguageId: target.id, isDefault: true },
  ]

  await request.patch(iam(TENANT_LANGUAGES_PATH), updates, buildTenantHeaderConfig(id))

  return getTenantById(id)
}

export async function createSettingKey(
  payload: SettingKeyCreateRequest,
): Promise<ApiResponse<void>> {
  const response = await request.post<ApiResponseEnvelope<null>>(
    iam(`${TENANT_SETTINGS_PATH}/setting-keys`),
    payload,
  )
  return {
    data: undefined,
    message: response.data?.message ?? i18next.t('tenant.settings.addSuccess'),
  }
}

export async function getTenantSetting(
  tenantSettingId: string,
): Promise<ApiResponse<TenantSettingDto>> {
  const response = await request.get<ApiResponseEnvelope<TenantSettingDto>>(
    iam(`${TENANT_SETTINGS_PATH}/${tenantSettingId}`),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function getTenantSettingByKey(
  tenantId: string,
  settingKey: string,
): Promise<ApiResponse<TenantSettingDto>> {
  const response = await request.get<ApiResponseEnvelope<TenantSettingDto>>(
    iam(`${TENANT_SETTINGS_PATH}/settings/${settingKey}`),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function listTenantSettings(
  tenantId: string,
  params?: { current?: number; pageSize?: number; keywords?: string },
): Promise<ApiResponse<TenantSettingResponse>> {
  const qs = params ? toQueryString(params as Record<string, unknown>) : ''
  const response = await request.get<ApiResponseEnvelope<TenantSettingResponse>>(
    iam(qs ? `${TENANT_SETTINGS_PATH}?${qs}` : `${TENANT_SETTINGS_PATH}`),
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: unwrapApiEnvelope(response.data),
  }
}

export async function updateTenantSettingValue(
  tenantId: string,
  settingKey: string,
  value: string,
): Promise<ApiResponse<void>> {
  const response = await request.put<ApiResponseEnvelope<null>>(
    iam(`${TENANT_SETTINGS_PATH}/settings/${settingKey}`),
    { settingValue: value },
    buildTenantHeaderConfig(tenantId),
  )
  return {
    data: undefined,
    message: response.data?.message ?? i18next.t('tenant.settings.updateSuccess'),
  }
}
